import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Button, Textfield, PasswordConditions, PasswordValidation, validatePassword, isPasswordValid, doPasswordsMatch } from '@flexo/atoms';
import styles from './forgot-password.module.scss';
import { useNavigate } from 'react-router-dom';
import { FirebaseService, IconWrapper, httpService } from '@flexo/general';
import { useTranslation } from 'react-i18next';
import { Player } from '@lottiefiles/react-lottie-player';
import { useDispatch } from 'react-redux';
import { ALERT } from '@flexo/reducers';

export enum ForgotPasswordStage {
  EnterEmail = 1,
  EnterRecoveryCode = 2,
  ResetPassword = 3,
}

export interface InputErrors {
  email: boolean;
  code: boolean;
  newPassword: boolean;
  confirmPassword: boolean;
}

const ENV = (import.meta as any).env;

export function ForgotPassword({ containedLabelTextfield }: any) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentStage, setCurrentStage] = useState<ForgotPasswordStage>(
    ForgotPasswordStage.EnterEmail
  );
  const [hasResent, setHasResent] = useState(false);

  const loader = '/loaderLottie.json';
  const [loading, setLoading] = useState(false);

  const [inputType, setInputType] = useState({
    newPassword: 'password',
    confirmPassword: 'password',
  });
  const [icon, setIcon] = useState({
    newPassword: 'visibility',
    confirmPassword: 'visibility',
  });

  const [passwordValidation, setPasswordValidation] =
    useState<PasswordValidation>({
      lengthValid: false,
      hasNumber: false,
      hasSpecialChar: false,
      hasUpperCase: false,
      hasLowerCase: false,
      passwordsMatch: false,
    });

  const [errors, setErrors] = useState<InputErrors>({
    email: false,
    code: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [blur, setBlur] = useState({
    email: false,
    code: false,
    confirmPassword: false,
    newPassword: false,
  });
  const [passwordBlur, setPasswordBlur] = useState(false);

  const browserLocale =
  navigator.language?.split('-')[0] || ENV.VITE_APP_LANGUAGE || 'en';

  // Create refs for all input fields
  const emailRef = useRef<HTMLInputElement>(null);
  const recoveryCodeRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  // Reset resent state when stage changes away from recovery code
  useEffect(() => {
    if (currentStage !== ForgotPasswordStage.EnterRecoveryCode) {
      setHasResent(false);
    }
  }, [currentStage]);

  // Focus appropriate input field when stage changes
  useEffect(() => {
    const focusTimer = setTimeout(() => {
      let targetRef: React.RefObject<HTMLInputElement> | null = null;

      if (currentStage === ForgotPasswordStage.EnterEmail) {
        targetRef = emailRef;
      } else if (currentStage === ForgotPasswordStage.EnterRecoveryCode) {
        targetRef = recoveryCodeRef;
      } else if (currentStage === ForgotPasswordStage.ResetPassword) {
        targetRef = passwordRef;
      }

      if (targetRef?.current) {
        targetRef.current.focus();
      }
    }, 250);

    return () => clearTimeout(focusTimer);
  }, [currentStage]);

  const isEmail = useCallback((email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  }, []);

  const isValidEmail = useCallback(
    (email: string) => {
      return isEmail(email);
    },
    [isEmail]
  );

  const isValidRecoveryCode = useCallback((code: string) => {
    return code.length >= 4;
  }, []);

  const handlePasswordValidation = useCallback(
    (password: string) => {
      const validation = validatePassword(password, confirmPassword);
      setPasswordValidation(validation);
    },
    [confirmPassword]
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      switch (name) {
        case 'email':
          setEmail(value);
          break;
        case 'recoveryCode':
          setRecoveryCode(value);
          break;
        case 'newPassword':
          setNewPassword(value);
          handlePasswordValidation(value);
          break;
        case 'confirmPassword':
          setConfirmPassword(value);
          setPasswordValidation((prev) => ({
            ...prev,
            passwordsMatch: newPassword === value,
          }));
          break;
      }
    },
    [newPassword, confirmPassword, handlePasswordValidation]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (name === 'newPassword') setPasswordBlur(true);

      if (name === 'confirmPassword') {
        setErrors({
          ...errors,
          confirmPassword: newPassword !== value,
        });
        setBlur({
          ...blur,
          confirmPassword: true,
        });
      }

      if (name !== 'email' || !value.length) return;
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: !isEmail(value),
      }));
    },
    [newPassword, confirmPassword, errors, blur, isEmail]
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { name } = event.target;

      if (name === 'email') {
        setErrors((prevErrors) => ({
          ...prevErrors,
          email: false,
        }));
      }

      if (name === 'newPassword') {
        setPasswordBlur(false);
      }

      if (name === 'confirmPassword') {
        setErrors((prevErrors) => ({
          ...prevErrors,
          confirmPassword: false,
        }));
      }
    },
    [blur]
  );
  const requestRecoveryCode = useCallback(async () => {
    if (!isEmail(email)) return;
    const firebaseService = new FirebaseService();
    const idToken = await firebaseService.getIdToken();
    setLoading(true);
    try {
      const response = await httpService.post({
        url: `${ENV.VITE_APP_BASE_URL}/auth/v1_1/password-forgot`,
        data: { email: email, locale: browserLocale },
        idToken: idToken,
      });
      if (response.status === 200)
        setCurrentStage(ForgotPasswordStage.EnterRecoveryCode);
    } catch (error) {
      dispatch(
        ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any })
      );
    } finally {
      setLoading(false);
    }
  }, [email, ENV.VITE_APP_BASE_URL, isEmail, dispatch, browserLocale]);

  const verifyRecoveryCode = useCallback(async () => {
    setLoading(true); // Start loading
    const firebaseService = new FirebaseService();

    const idToken = await firebaseService.getIdToken();
    try {
      const response = await httpService.post({
        url: `${ENV.VITE_APP_BASE_URL}/auth/v1_1/validate-reset-code`,
        data: {
          email: email,
          reset_code: recoveryCode,
        },
      });

      if (response.data.valid)
        setCurrentStage(ForgotPasswordStage.ResetPassword);
      else
        dispatch(
          ALERT.SetAlert({
            payload: {
              value: {
                type: 'BANNER_INVALID_CREDENTIALS',
              } as any,
            },
          } as any)
        );

      return response;
    } catch (error) {
      // console.warn(error);

      dispatch(
        ALERT.SetAlert({
          payload: {
            value: {
              type: 'BANNER_UNEXPECTED_ERROR',
            } as any,
          },
        } as any)
      );
    } finally {
      setLoading(false); // Stop loading after action completes
    }
  }, [email, recoveryCode, ENV.VITE_APP_BASE_URL]);

  const resetPassword = useCallback(async (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    const firebaseService = new FirebaseService();
    const idToken = await firebaseService.getIdToken();
    if (newPassword !== confirmPassword) return;
    setLoading(true);

    try {
      const response = await httpService.post({
        url: `${ENV.VITE_APP_BASE_URL}/auth/v1_1/password-change-confirm`,
        data: {
          email: email,
          reset_code: recoveryCode,
          new_password: newPassword,
          tenant_id: 'internal-foj18',
        },
        idToken: idToken,
      });
      if (response.status === 200)
        localStorage.setItem('passwordUpdated', 'true');
      sessionStorage.setItem('passwordUpdated', 'true');
      navigate('/login');


    } catch (error: any) {
      console.error('Password reset failed:', error);

      // Check if error is related to invalid recovery code or email combination
      if (
        error?.response?.status === 400 ||
        error?.response?.data?.message?.includes('reset_code') ||
        error?.response?.data?.message?.includes('Invalid')
      ) {
        // Invalid recovery code/email combination - go back to recovery code stage with error
        setCurrentStage(ForgotPasswordStage.EnterRecoveryCode);
        setErrors((prevErrors) => ({ ...prevErrors, code: true }));
        dispatch(
          ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any })
        );
      } else {
        // Other errors - show generic banner
        dispatch(
          ALERT.SetAlert({ value: { type: 'BANNER_UNEXPECTED_ERROR' } as any })
        );
      }
    } finally {
      setLoading(false);
    }


  }, [
    email,
    recoveryCode,
    newPassword,
    confirmPassword,
    ENV.VITE_APP_BASE_URL,
    navigate,
    dispatch,
  ]);

  const togglePasswordVisibility = useCallback((name: string) => {
    setInputType((p) => ({
      ...p,
      [name]: p[name] === 'password' ? 'text' : 'password',
    }));
    setIcon((p) => ({
      ...p,
      [name]: p[name] === 'hide-visibility' ? 'visibility' : 'hide-visibility',
    }));
  }, []);

  const handleResendCode = useCallback(async () => {
    await requestRecoveryCode();
    setHasResent(true);
  }, [requestRecoveryCode]);





  const renderCustomSubtitle = useCallback(() => {


    if (currentStage !== ForgotPasswordStage.EnterRecoveryCode) return null;

    return (
      <div className={styles.ForgotPassword__RecoveryText}>
        <p>
          {t('pages.forgotten_password.recoveryCode_subtitle.line_1')}{' '}
          <strong>{email}</strong>{' '}
          {t('pages.forgotten_password.recoveryCode_subtitle.line_2')}
        </p>
        <p>
          {t('pages.forgotten_password.recoveryCode_subtitle.line_3')}{' '}
          {hasResent ? (
            <span className={styles.ForgotPassword__ResentText}>
              {t('pages.forgotten_password.recoveryCode_subtitle.resentLabel')}
              <IconWrapper iconName="check-encircled_outline" />
            </span>
          ) : (
            <button
              onClick={handleResendCode}
              className={styles.ForgotPassword__ResendButton}
            >
              {t('pages.forgotten_password.recoveryCode_subtitle.buttonLabel')}
            </button>
          )}
        </p>
      </div>
    );
  }, [currentStage, t, email, hasResent, handleResendCode]);

  const stageConfig = useMemo(
    () => ({
      [ForgotPasswordStage.EnterEmail]: {
        title: t('pages.forgot_password.data.title'),
        subtitle: t('pages.forgot_password.data.subtitle'),
        buttonLabel: t('pages.forgot_password.data.buttonLabel'),
        action: requestRecoveryCode,
        isDisabled: () => !email || !isValidEmail(email) || loading,
        inputComponent: (
          <form className={styles.ForgotPassword__Form}>
            <Textfield
              name="email"
              ref={emailRef}
              value={email}
              placeholder={
                t('pages.forgot_password.data.inputs.0.placeholder') as string
              }
              label={t('pages.forgot_password.data.inputs.0.label') as string}
              onChange={handleInputChange}
              type="email"
              containedLabel={false}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              onBlur={handleBlur}
              onFocus={handleFocus}
              onPaste={(e) => false}
              onCopy={(e) => false}
            />
          </form>
        ),
      },
      [ForgotPasswordStage.EnterRecoveryCode]: {
        title: t('pages.recovery_code_data.data.title'),
        subtitle: null,
        buttonLabel: t('pages.recovery_code_data.data.buttonLabel'),
        action: verifyRecoveryCode,
        isDisabled: () =>
          !recoveryCode || !isValidRecoveryCode(recoveryCode) || loading,
        inputComponent: (
          <form className={styles.ForgotPassword__Form}>
            <Textfield
              name="recoveryCode"
              ref={recoveryCodeRef}
              value={recoveryCode}
              placeholder={
                t(
                  'pages.recovery_code_data.data.inputs.0.placeholder'
                ) as string
              }
              label={
                t('pages.recovery_code_data.data.inputs.0.label') as string
              }
              onChange={handleInputChange}
              type="text"
              containedLabel={false}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              onBlur={handleBlur}
              onFocus={handleFocus}
              onPaste={(e) => false}
              onCopy={(e) => false}
            />
          </form>
        ),
      },
      [ForgotPasswordStage.ResetPassword]: {
        title: t('pages.password_reset_web.title'),
        subtitle: t('pages.password_reset_web.subtitle', { email }),
        buttonLabel: t('pages.password_reset_web.buttonLabel'),
        action: resetPassword,
        isDisabled: () => {
          if (!newPassword || !confirmPassword) return true;
          return (
            !isPasswordValid(newPassword) ||
            !doPasswordsMatch(newPassword, confirmPassword) ||
            loading
          );
        },
        inputComponent: (
          <form className={styles.ForgotPassword__Form}>
            <Textfield
              name="newPassword"
              ref={passwordRef}
              value={newPassword}
              placeholder={
                t(
                  'pages.password_reset_web.inputs.password.placeholder'
                ) as string
              }
              label={
                t('pages.password_reset_web.inputs.password.label') as string
              }
              onChange={handleInputChange}
              type={inputType.newPassword}
              icon={icon.newPassword}
              onIconClick={() => togglePasswordVisibility('newPassword')}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onPaste={(e) => false}
              onCopy={(e) => false}
              containedLabel={false}
            />
            <PasswordConditions
              className={styles.PasswordConditions}
              validation={passwordValidation}
              isFocused={!passwordBlur}
              conditionClassName={styles.ForgotPassword__Conditions}
            />
            <Textfield
              variant="default"
              name="confirmPassword"
              ref={confirmPasswordRef}
              value={confirmPassword}
              placeholder={
                t(
                  'pages.password_reset_web.inputs.password_confirm.placeholder'
                ) as string
              }
              label={
                t(
                  'pages.password_reset_web.inputs.password_confirm.label'
                ) as string
              }
              onChange={handleInputChange}
              type={inputType.confirmPassword}
              icon={icon.confirmPassword}
              onIconClick={() => togglePasswordVisibility('confirmPassword')}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onPaste={(e) => false}
              onCopy={(e) => false}
              containedLabel={false}
            />
          </form>
        ),
      },
    }),
    [
      t,
      email,
      isValidEmail,
      isValidRecoveryCode,
      isPasswordValid,
      doPasswordsMatch,
      handleInputChange,
      handleBlur,
      handleFocus,
      recoveryCode,
      newPassword,
      confirmPassword,
      inputType,
      icon,
      togglePasswordVisibility,
      passwordValidation,
      passwordBlur,
      requestRecoveryCode,
      verifyRecoveryCode,
      resetPassword,
      loading,
    ]
  );

  const currentStageConfig = useMemo(
    () => stageConfig[currentStage],
    [stageConfig, currentStage]
  );

  const handleKeyPress = useCallback(
    
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (!currentStageConfig.isDisabled() && !loading) {
          event.preventDefault();
          event.stopPropagation();

          (currentStageConfig as any).action();
        }
      }
        
    },
    [currentStageConfig, loading]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  return (
    <div className={styles.ForgotPassword}>
      <div className={styles.ForgotPassword__Logo}>
        <IconWrapper iconName="logo_wide" />
      </div>
      <div className={styles.ForgotPassword__Content}>
        <div className={styles.ForgotPassword__Header}>
          <div className={styles.ForgotPassword__Header__Title}>
            {currentStageConfig.title}
          </div>
          {currentStageConfig.subtitle && (
            <div className={styles.ForgotPassword__Header__Subtitle}>
              {currentStageConfig.subtitle}
            </div>
          )}
          {renderCustomSubtitle()}
        </div>

        {currentStageConfig.inputComponent}

        {currentStage === ForgotPasswordStage.EnterEmail ? (
          <div className={styles.ForgotPassword__FooterWithLogin}>
            <div className={styles.ForgotPassword__BackToLogin}>
              <span>{t('pages.forgot_password.data.return_label.label')}</span>
              <button
                onClick={() => navigate('/login')}
                className={styles.ForgotPassword__BackToLogin__Button}
              >
                {t('pages.forgot_password.data.return_label.buttonLabel')}
              </button>
            </div>
            <Button
              className={styles.ForgotPassword__Footer__Button}
              onClick={currentStageConfig.action}
              variant="primary"
              color="secondary"
              label={
                loading ? (
                  <Player
                    autoplay
                    loop
                    src={loader}
                    style={{ height: 20, transform: 'scale(0.7)' }}
                  />
                ) : (
                  currentStageConfig.buttonLabel
                )
              }
              disabled={currentStageConfig.isDisabled()}
            />
          </div>
        ) : (
          <div className={styles.ForgotPassword__Footer}>
            <Button
              className={styles.ForgotPassword__Footer__Button}
              onClick={currentStageConfig.action}
              variant="primary"
              color="secondary"
              label={
                loading ? (
                  <Player
                    autoplay
                    loop
                    src={loader}
                    style={{ height: 20, transform: 'scale(0.7)' }}
                  />
                ) : (
                  currentStageConfig.buttonLabel
                )
              }
              disabled={currentStageConfig.isDisabled()}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;
